// components/ScheduledNotificationModal.js
import { useState, useEffect, useRef } from "react";
import {
  X,
  Plus,
  Trash2,
  Loader2,
  Check,
  AlertCircle,
  Share2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "./ui/switch";
import { extractArticleIdFromUrl } from "../lib/utils";

const baseUrl =
  process.env.REACT_APP_NOTIFICATION_BASE_URL || "http://localhost:8080";

const daysOfWeek = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const displayDays = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};

const MAX = {
  TITLE: 100,
  BODY: 200,
};

export default function ScheduledNotificationModal({
  isOpen,
  onClose,
  onSave,
  domainInfo,
  topics = [],
  topicsLoading = false,
  topicsError = null,
  user='user',
}) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [url, setUrl] = useState("");
  const [img, setImg] = useState("");
  const [banner, setBanner] = useState("");
  const [selectedTopicIds, setSelectedTopicIds] = useState([]);
  const [utmSource, setUtmSource] = useState("");
  const [utmMedium, setUtmMedium] = useState("");
  const [utmCampaign, setUtmCampaign] = useState("");
  const [actionButtons, setActionButtons] = useState([]);
  const getDefaultHour = () => {
    const now = new Date();
    return now.getHours().toString().padStart(2, "0");
  };
  const getDefaultToday = () => {
    const now = new Date();
    return now.toISOString().split("T")[0];
  };
  const getDefaultDay = () => {
    const now = new Date();
    const dayIndex = now.getDay();
    // Convert JavaScript day (0-6, Sun-Sat) to our daysOfWeek index (0-6, Mon-Sun)
    const dayOfWeek = dayIndex === 0 ? 6 : dayIndex - 1;
    return [daysOfWeek[dayOfWeek]];
  };

  const [hour, setHour] = useState(getDefaultHour());
  const [minute, setMinute] = useState("00");
  const [selectedDays, setSelectedDays] = useState(getDefaultDay());
  const [startDate, setStartDate] = useState(getDefaultToday());
  const [endDate, setEndDate] = useState(getDefaultToday());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [metadataError, setMetadataError] = useState(null);
  /* ---------- SHARE PLATFORMS ---------- */
  const SHARE_PLATFORMS = [
    { id: "whatsapp", label: "WhatsApp", icon: "W" },
    { id: "facebook", label: "Facebook", icon: "F" },
    { id: "twitter", label: "Twitter/X", icon: "X" },
    { id: "linkedin", label: "LinkedIn", icon: "in" },
    { id: "telegram", label: "Telegram", icon: "T" },
  ];

  const debounceRef = useRef(null);

  // Prefill UTM & banner
  useEffect(() => {
    if (!domainInfo) return;
    setUtmSource((prev) => prev || domainInfo.utmSource || "app");
    setUtmMedium((prev) => prev || domainInfo.utmMedium || "notification");
    setUtmCampaign((prev) => prev || domainInfo.utmCampaign || "");
    if (!img) setImg(domainInfo.image || "");
  }, [domainInfo, img]);

  // Reset form
  const resetForm = () => {
    setTitle("");
    setBody("");
    setUrl("");
    setImg(domainInfo?.image || "");
    setBanner("");
    setSelectedTopicIds([]);
    setUtmSource(domainInfo?.utmSource || "");
    setUtmMedium(domainInfo?.utmMedium || "");
    setUtmCampaign(domainInfo?.utmCampaign || "");
    setActionButtons([]);
    setHour(getDefaultHour());
    setMinute("00");
    setSelectedDays(getDefaultDay());
    setStartDate(getDefaultToday());
    setEndDate(getDefaultToday());
    setSubmitError(null);
    setMetadataError(null);
  };

  // Metadata scraping
  useEffect(() => {
    const fetchMetadata = async () => {
      if (!url || !/^https?:\/\//i.test(url)) return;

      setMetadataLoading(true);
      setMetadataError(null);

      try {
        const res = await fetch(
          `${baseUrl}notification/scrape-metadata?url=${encodeURIComponent(
            url
          )}`
        );
        if (!res.ok) throw new Error("Failed to fetch metadata");
        const json = await res.json();
        if (json.status !== 200) throw new Error(json.message || "API error");

        const { title: metaTitle, description, ogImage, favicon } = json.data;

        if (metaTitle && !title) setTitle(metaTitle.slice(0, MAX.TITLE));
        if (description && !body) setBody(description.slice(0, MAX.BODY));
        if (ogImage && !banner) setBanner(ogImage);
        if (favicon && !img) setImg(favicon);
        if (!utmCampaign) {
          setUtmCampaign(extractArticleIdFromUrl(url));
        }
      } catch (e) {
        setMetadataError(e.message);
      } finally {
        setMetadataLoading(false);
      }
    };

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fetchMetadata, 800);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [url]);

  const toggleTopic = (id) => {
    setSelectedTopicIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleDay = (day) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const addButton = () => {
    if (actionButtons.length >= 2) return;
    setActionButtons([...actionButtons, { title: "", url: "", action: "" }]);
  };

  const removeButton = (idx) => {
    setActionButtons((b) => b.filter((_, i) => i !== idx));
  };

  const updateButton = (idx, field, value) => {
    const newButtons = [...actionButtons];
    newButtons[idx][field] = value;
    setActionButtons(newButtons);
  };

  const validate = () => {
    if (!title.trim()) return "Title is required";
    if (!url.trim()) return "URL is required";
    if (selectedTopicIds.length === 0) return "Select at least one campaign";
    if (selectedDays.length === 0) return "Select at least one day";
    if (!startDate || !endDate) return "Start and end dates are required";
    if (new Date(startDate) > new Date(endDate))
      return "End date must be after start date";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setSubmitError(validationError);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // === 1. Extract article ID from URL ===
      let articleId = null;
      try {
        const urlObj = new URL(url);
        const match = urlObj.pathname.match(/(\d+)\.html$/);
        if (match) articleId = match[1]; // e.g., "40017458"
      } catch (err) {
        console.warn("Could not parse URL for ID:", url);
      }

      // === 2. Build UTM params ===
      const utmParams = new URLSearchParams();

      if (utmSource) utmParams.append("utm_source", utmSource);
      if (utmMedium) utmParams.append("utm_medium", utmMedium);

      // Use user-defined campaign OR fallback to article ID
      const finalCampaign = utmCampaign || articleId;
      if (finalCampaign) {
        utmParams.append("utm_campaign", finalCampaign);
      }

      // === 3. Build final URL with UTM appended ===
      let finalUrl = url;
      if (utmParams.toString()) {
        const separator = url.includes("?") ? "&" : "?";
        finalUrl = `${url}${separator}${utmParams.toString()}`;
      }
      const processedButtons = actionButtons.map((btn, index) => {
        if (btn.shareId) {
          const shareUrl = buildShareUrl(
            btn.shareId,
            finalUrl,
            `${title} – ${body}`
          );
          return {
            title: btn.title,
            url: shareUrl,
            action: `btn${index + 1}`,
          };
        }
        return {
          title: btn.title,
          url: btn.url,
          action: btn.action,
        };
      });

      // === 4. Build payload ===
      const payload = {
        title,
        body,
        url: finalUrl, // UTM + ID appended
        img,
        banner,
        domain: domainInfo?.domain,
        utm_source: utmSource,
        utm_medium: utmMedium,
        utm_campaign: utmCampaign, // original (may be empty)
        domainId: domainInfo?.id,
        topicId: selectedTopicIds,
        preferences: [],
        language: domainInfo?.language,
        btn_list: processedButtons,
        scheduledDays: selectedDays,
        scheduledTime: `${hour}:${minute}`,
        createdBy: user,
        startDate,
        endDate,
      };

      // === 5. Send request ===
      const response = await fetch(
        `${baseUrl}notification/schedule/notification`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const result = await response.json();

        throw new Error(result.error || "Failed to schedule notification");
      }
      const result = await response.json();
      if (result.status !== 200) throw new Error(result.message || "API error");

      resetForm();
      onSave(result.data);
      onClose();
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };
  const toggleShare = (idx, platformId) => {
    const btn = actionButtons[idx];
    if (!btn) return;

    const newButtons = [...actionButtons];

    // If the button is already a share button for a *different* platform → just switch
    if (btn.shareId && btn.shareId !== platformId) {
      newButtons[idx] = {
        ...newButtons[idx],
        shareId: platformId,
        title: platformId === "twitter" ? "Tweet" : `Share on ${platformId}`,
        url: "",
      };
      setActionButtons(newButtons);
      return;
    }

    // Count how many share buttons are currently ON
    const enabledCount = actionButtons.filter((b) => !!b.shareId).length;

    if (btn.shareId === platformId) {
      // ---- TURN OFF ----
      newButtons[idx] = {
        title: "",
        url: "",
        action: btn.action.startsWith("btn") ? btn.action : `btn${idx + 1}`,
      };
    } else {
      // ---- TURN ON ----
      if (enabledCount >= 2) {
        // disable the oldest enabled share
        const oldestIdx = actionButtons.findIndex((b) => !!b.shareId);
        if (oldestIdx !== -1 && oldestIdx !== idx) {
          newButtons[oldestIdx] = {
            title: "",
            url: "",
            action: btn.action.startsWith("btn") ? btn.action : `btn${idx + 1}`,
          };
        }
      }

      newButtons[idx] = {
        ...newButtons[idx],
        shareId: platformId,
        title:
          platformId === "twitter" ? "Share on X" : `Share on ${platformId}`,
        url: "",
        action: btn.action.startsWith("btn") ? btn.action : `btn${idx + 1}`,
      };
    }

    setActionButtons(newButtons);
  };
  const buildShareUrl = (platform, pageUrl, text) => {
    const encUrl = encodeURIComponent(pageUrl);
    const encText = encodeURIComponent(text);
    const map = {
      whatsapp: `https://api.whatsapp.com/send?text=${encText}%20${encUrl}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encUrl}`,
      twitter: `https://twitter.com/intent/tweet?url=${encUrl}&text=${encText}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encUrl}`,
      telegram: `https://t.me/share/url?url=${encUrl}&text=${encText}`,
    };
    return map[platform];
  };
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col md:flex-row items-center justify-center p-4 overflow-y-auto gap-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-card rounded-2xl shadow-xl min-h-[400px] w-full max-w-2xl border border-border my-8 h-[90%] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-xl font-bold text-foreground">
            Schedule Notification
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg p-2 hover:bg-secondary transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Errors */}
          {submitError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              {submitError}
            </div>
          )}
          {metadataError && (
            <div className="bg-orange-50 border border-orange-200 text-orange-700 px-4 py-3 rounded-lg flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Metadata: {metadataError}
            </div>
          )}
          {metadataLoading && (
            <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Fetching metadata...
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* URL */}
            <div className="md:col-span-2 space-y-2">
              <Label htmlFor="url">URL *</Label>
              <Input
                id="url"
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com/article"
              />
            </div>

            {/* Title */}
            <div className="md:col-span-2 space-y-2">
              <Label htmlFor="title">
                Notification Title * ({title.length}/{MAX.TITLE})
              </Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value.slice(0, MAX.TITLE))}
                placeholder="Enter title"
              />
            </div>

            {/* Body */}
            <div className="md:col-span-2 space-y-2">
              <Label htmlFor="body">
                Body * ({body.length}/{MAX.BODY})
              </Label>
              <Input
                id="body"
                value={body}
                onChange={(e) => setBody(e.target.value.slice(0, MAX.BODY))}
                placeholder="Short description"
              />
            </div>

            {/* Images */}
            <div className="space-y-2">
              <Label htmlFor="img">Notification Image</Label>
              <Input
                id="img"
                type="url"
                value={img}
                onChange={(e) => setImg(e.target.value)}
                placeholder="https://example.com/icon.png"
                disabled
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="banner">Banner Image</Label>
              <Input
                id="banner"
                type="url"
                value={banner}
                onChange={(e) => setBanner(e.target.value)}
                placeholder="https://example.com/banner.jpg"
              />
            </div>

            {/* Campaigns */}
            <div className="md:col-span-2 space-y-2">
              <Label>Campaigns *</Label>
              {topicsLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading campaigns...
                </div>
              ) : topicsError ? (
                <div className="text-red-600">{topicsError}</div>
              ) : (
                <>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-between h-auto min-h-[40px] text-left font-normal"
                      >
                        {selectedTopicIds.length === 0
                          ? "Select campaigns"
                          : `${selectedTopicIds.length} selected`}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0">
                      <div className="max-h-64 overflow-y-auto p-2">
                        {topics.map((t) => (
                          <label
                            key={t.id}
                            className="flex items-center space-x-2 p-2 hover:bg-secondary rounded cursor-pointer"
                          >
                            <Checkbox
                              checked={selectedTopicIds.includes(t.id)}
                              onCheckedChange={() => toggleTopic(t.id)}
                            />
                            <span>{t.name}</span>
                          </label>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>

                  {selectedTopicIds.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {selectedTopicIds.map((id) => {
                        const topic = topics.find((t) => t.id === id);
                        return topic ? (
                          <span
                            key={id}
                            className="inline-flex items-center gap-1 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm"
                          >
                            {topic.name}
                            <button
                              type="button"
                              onClick={() => toggleTopic(id)}
                              className="hover:bg-primary/20 rounded-full p-0.5"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ) : null;
                      })}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* UTM */}
            <div className="space-y-2">
              <Label htmlFor="utm_source">UTM Source</Label>
              <Input
                id="utm_source"
                value={utmSource}
                onChange={(e) => setUtmSource(e.target.value)}
                placeholder="app"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="utm_medium">UTM Medium</Label>
              <Input
                id="utm_medium"
                value={utmMedium}
                onChange={(e) => setUtmMedium(e.target.value)}
                placeholder="notification"
              />
            </div>
            <div className="md:col-span-2 space-y-2">
              <Label htmlFor="utm_campaign">UTM Campaign</Label>
              <Input
                id="utm_campaign"
                value={utmCampaign}
                onChange={(e) => setUtmCampaign(e.target.value)}
                placeholder="e.g. summer_sale"
              />
            </div>
          </div>

          {/* Schedule Settings */}
          <div className="p-4 border border-border rounded-lg space-y-4">
            <h3 className="font-semibold text-foreground">Schedule Settings</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Time *</Label>
                <div className="flex gap-2">
                  <Select value={hour} onValueChange={setHour}>
                    <SelectTrigger className="flex-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 24 }, (_, i) =>
                        i.toString().padStart(2, "0")
                      ).map((h) => (
                        <SelectItem key={h} value={h}>
                          {h}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="flex items-center">:</span>
                  <Select value={minute} onValueChange={setMinute}>
                    <SelectTrigger className="flex-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {["00", "15", "30", "45"].map((m) => (
                        <SelectItem key={m} value={m}>
                          {m}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Days *</Label>
                <div className="flex flex-wrap gap-2">
                  {daysOfWeek.map((day) => (
                    <Button
                      key={day}
                      type="button"
                      variant={
                        selectedDays.includes(day) ? "default" : "outline"
                      }
                      size="sm"
                      onClick={() => toggleDay(day)}
                    >
                      {displayDays[day].slice(0, 3)}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="startDate">Start Date *</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="endDate">End Date *</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          {/* Action Buttons */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Action Buttons (Max 2)</Label>
              {actionButtons.length < 2 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addButton}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Button
                </Button>
              )}
            </div>

            {actionButtons.map((btn, i) => (
              <div
                key={i}
                className="flex gap-3 p-4 border border-border rounded-lg"
              >
                <div className="flex-1 space-y-3">
                  {/* Normal fields – hidden when share is active */}
                  {!btn.shareId && (
                    <>
                      <Input
                        placeholder="Button title"
                        value={btn.title}
                        onChange={(e) =>
                          updateButton(i, "title", e.target.value)
                        }
                      />
                      <Input
                        type="url"
                        placeholder="Button URL"
                        value={btn.url}
                        onChange={(e) => updateButton(i, "url", e.target.value)}
                      />
                      <Input
                        placeholder="Action (optional)"
                        value={btn.action}
                        onChange={(e) =>
                          updateButton(i, "action", e.target.value)
                        }
                      />
                    </>
                  )}

                  {/* Share switches */}
                  {/* <div className="flex items-center gap-3 flex-wrap pt-1">
                    <Share2 className="h-4 w-4 text-muted-foreground" />
                    {SHARE_PLATFORMS.map((p) => (
                      <div
                        key={p.id}
                        className="flex items-center gap-1.5 cursor-pointer select-none"
                      >
                        <Switch
                          checked={btn.shareId === p.id}
                          onCheckedChange={() => toggleShare(i, p.id)}
                        />
                        <span className="text-xs font-medium text-foreground">
                          {p.label}
                        </span>
                      </div>
                    ))}
                  </div> */}

                  {/* Show auto title */}
                  {btn.shareId && (
                    <p className="text-sm text-muted-foreground">{btn.title}</p>
                  )}
                </div>

                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeButton(i)}
                  className="text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>

          {/* Submit */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={isSubmitting || metadataLoading}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Scheduling...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Schedule
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
      <div className="relative bg-[#f1f1f2] px-4 py-4 w-[500px]">
        {banner && (
          <div className="mb-2">
            <img
              width={"100%"}
              height={100}
              src={banner ?? "/default.png"}
              tile="banner"
              alt="banner"
              style={{ height: "200px" }}
            />
          </div>
        )}
        <div className="flex flex-row gap-2 ">
          <div className="w-[20%]">
            {" "}
            <img
              height={150}
              width={150}
              src={img ?? "/default.png"}
              title="logo"
              alt="logo"
            />
          </div>
          <div className="w-[75%]">
            <h2 className="text-xl font-bold text-foreground wrap ">
              {title ||
                "Lorem ipsum dolor sit amet consectetur adipiscing elit quisque "}
            </h2>
            <p className="text-sm text-gray">
              {body ||
                "Lorem ipsum dolor sit amet consectetur adipiscing elit quisque Lorem ipsum dolor sit amet consectetur adipiscing elit quisque"}
            </p>
          </div>
        </div>
        {actionButtons.length > 0 && (
          <div className="flex flex-row gap-4 justify-between mt-2 flex-grow">
            {actionButtons.map((el) => {
              return (
                <a
                  className="bg-[#f5f5f5] border border-gray-300 shadow flex-grow text-center p-1"
                  url={el.url}
                  target="_blank"
                >
                  {el.title ?? "Lorem ipsum"}
                </a>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
